import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AppContext } from "../../context/AppContext";
import { Switch } from "antd";
import { toast } from "react-toastify";
import { HashLoader } from "react-spinners";
import * as Fi from "react-icons/fi";
import TablePagination, {
  DEFAULT_PAGE_SIZE,
  paginateList,
} from "./TablePagination";

const EMPTY_FORM = {
  tableID: "",
  name: "",
  chairs: 4,
  status: "Active",
  tableType: "dining",
};

function DiningTablesPanel() {
  const {
    diningTables,
    diningTablesLoading,
    fetchDiningTables,
    createDiningTable,
    updateDiningTable,
    updateDiningTableStatus,
    fetchAllTables,
  } = useContext(AppContext);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [page, setPage] = useState(1);
  const [statusBusyId, setStatusBusyId] = useState(null);

  useEffect(() => {
    fetchDiningTables();
  }, []);

  useEffect(() => {
    if (!sheetOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheetOpen]);

  const paged = useMemo(
    () => paginateList(diningTables, page, DEFAULT_PAGE_SIZE),
    [diningTables, page]
  );

  useEffect(() => {
    setPage(1);
  }, [diningTables]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setSheetOpen(true);
  };

  const openEdit = (table) => {
    setEditingId(table?.id ?? table?.tableID ?? null);
    setForm({
      tableID: table?.tableID ?? "",
      name: table?.name ?? "",
      chairs: Number(table?.chairs) || 1,
      status: table?.status === "Inactive" ? "Inactive" : "Active",
      tableType:
        String(table?.tableType || "dining").toLowerCase() === "takeaway"
          ? "takeaway"
          : "dining",
    });
    setFormError("");
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!String(form.tableID || "").trim()) return "Table ID is required.";
    if (!String(form.name || "").trim()) return "Table name is required.";
    if (!form.chairs || Number(form.chairs) <= 0) {
      return "Chairs must be greater than 0.";
    }
    if (!["Active", "Inactive"].includes(form.status)) return "Invalid status.";
    if (!["dining", "takeaway"].includes(form.tableType)) {
      return "Invalid table type.";
    }
    return "";
  };

  const buildPayload = () => ({
    tableID: String(form.tableID).trim(),
    name: String(form.name).trim(),
    chairs: Number(form.chairs),
    status: form.status,
    tableType: form.tableType,
  });

  const refreshTables = async () => {
    await fetchDiningTables();
    await fetchAllTables?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) {
      setFormError(error);
      toast.error(error);
      return;
    }

    const payload = buildPayload();

    if (editingId) {
      await updateDiningTable(editingId, payload, {
        onSuccess: async () => {
          toast.success("Table updated successfully!");
          closeSheet();
          await refreshTables();
        },
        onFailed: (err) => {
          toast.error(err?.messages || err?.message || "Update failed.");
        },
      });
      return;
    }

    await createDiningTable(payload, {
      onSuccess: async () => {
        toast.success("Table created successfully!");
        closeSheet();
        await refreshTables();
      },
      onFailed: (err) => {
        toast.error(err?.messages || err?.message || "Create failed.");
      },
    });
  };

  const handleToggleStatus = async (table, checked) => {
    const id = table?.id ?? table?.tableID;
    if (!id) {
      toast.error("Table id is missing.");
      return;
    }

    const nextStatus = checked ? "Active" : "Inactive";
    setStatusBusyId(String(id));

    try {
      await updateDiningTableStatus(id, nextStatus, {
        onSuccess: async () => {
          toast.success(
            nextStatus === "Active" ? "Table enabled." : "Table disabled."
          );
          await refreshTables();
        },
        onFailed: (err) => {
          toast.error(
            err?.messages || err?.message || "Status update failed."
          );
        },
      });
    } finally {
      setStatusBusyId(null);
    }
  };

  const sheet =
    sheetOpen && typeof document !== "undefined"
      ? createPortal(
          <div className="dt-sheet" role="presentation">
            <button
              type="button"
              className="dt-sheet__backdrop"
              aria-label="Close sheet"
              onClick={closeSheet}
            />
            <aside
              className="dt-sheet__panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="dt-sheet-title"
            >
              <div className="dt-sheet__handle" aria-hidden />
              <header className="dt-sheet__header">
                <h2 id="dt-sheet-title" className="dt-sheet__title">
                  {editingId ? "Edit dining table" : "Add dining table"}
                </h2>
                <button
                  type="button"
                  className="dt-sheet__close"
                  onClick={closeSheet}
                  aria-label="Close"
                >
                  <Fi.FiX />
                </button>
              </header>

              <form className="dt-form dt-sheet__body" onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="tableID">
                    Table ID
                  </label>
                  <input
                    id="tableID"
                    name="tableID"
                    className="form-control"
                    value={form.tableID}
                    onChange={onFormChange}
                    placeholder="e.g. 12"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="name">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    className="form-control"
                    value={form.name}
                    onChange={onFormChange}
                    placeholder="e.g. Window Table"
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="chairs">
                    Chairs
                  </label>
                  <input
                    id="chairs"
                    name="chairs"
                    type="number"
                    min={1}
                    className="form-control"
                    value={form.chairs}
                    onChange={onFormChange}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="tableType">
                    Table type
                  </label>
                  <select
                    id="tableType"
                    name="tableType"
                    className="form-select"
                    value={form.tableType}
                    onChange={onFormChange}
                  >
                    <option value="dining">Dining</option>
                    <option value="takeaway">Takeaway</option>
                  </select>
                </div>
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="status">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    className="form-select"
                    value={form.status}
                    onChange={onFormChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                {formError ? <p className="err__msg mb-3">{formError}</p> : null}

                <div className="dt-form__actions">
                  <button
                    type="button"
                    className="el-card__btn el-card__btn--ghost"
                    onClick={closeSheet}
                    disabled={diningTablesLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="el-card__btn el-card__btn--primary"
                    disabled={diningTablesLoading}
                  >
                    {diningTablesLoading
                      ? "Please wait…"
                      : editingId
                        ? "Save changes"
                        : "Add table"}
                  </button>
                </div>
              </form>
            </aside>
          </div>,
          document.body
        )
      : null;

  return (
    <Fragment>
      {sheet}

      <section className="rs-settings-card">
        <div className="rs-settings-closures__bar">
          <div>
            <h2 className="rs-settings-card__title" style={{ margin: 0 }}>
              Table management
            </h2>
            <p className="rs-settings-card__meta">
              {diningTables.length} table
              {diningTables.length === 1 ? "" : "s"} configured
            </p>
          </div>
          <div className="dt-panel__actions">
            <button
              type="button"
              className="rs-settings__ghost"
              onClick={fetchDiningTables}
              disabled={diningTablesLoading}
            >
              <Fi.FiRefreshCw /> Refresh
            </button>
            <button
              type="button"
              className="rs-settings__edit rs-settings__edit--sm"
              onClick={openCreate}
            >
              <Fi.FiPlus /> Add table
            </button>
          </div>
        </div>

        {diningTablesLoading && diningTables.length === 0 ? (
          <div className="rs-settings__loader">
            <HashLoader color="#f44336" />
          </div>
        ) : paged.total === 0 ? (
          <div className="rs-settings-empty-card">
            <Fi.FiGrid className="rs-settings-empty-card__icon" />
            <p className="rs-settings-empty-card__title">No tables yet</p>
            <p className="rs-settings-empty-card__text">
              Add your first dining table to start seating guests.
            </p>
            <button
              type="button"
              className="rs-settings__edit"
              onClick={openCreate}
            >
              <Fi.FiPlus /> Add table
            </button>
          </div>
        ) : (
          <>
            <div className="rs-settings-table-wrap dt-table-desktop">
              <table className="rs-settings-table">
                <thead>
                  <tr>
                    <th>Table ID</th>
                    <th>Name</th>
                    <th>Chairs</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.items.map((table) => {
                    const rowId = table?.id ?? table?.tableID;
                    const isActive = table?.status === "Active";
                    return (
                      <tr key={rowId}>
                        <td>
                          <span className="dt-id">{table?.tableID || "—"}</span>
                        </td>
                        <td className="dt-name">{table?.name || "—"}</td>
                        <td>{table?.chairs ?? "—"}</td>
                        <td>
                          <span className="dt-type-chip">
                            {table?.tableType || "—"}
                          </span>
                        </td>
                        <td>
                          <Switch
                            checked={isActive}
                            loading={
                              statusBusyId != null &&
                              String(statusBusyId) === String(rowId)
                            }
                            onChange={(checked) =>
                              handleToggleStatus(table, checked)
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="dt-edit-btn"
                            onClick={() => openEdit(table)}
                            aria-label={`Edit ${table?.name || "table"}`}
                          >
                            <Fi.FiEdit3 /> Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="dt-card-list" aria-label="Dining tables">
              {paged.items.map((table) => {
                const rowId = table?.id ?? table?.tableID;
                const isActive = table?.status === "Active";
                return (
                  <article key={rowId} className="dt-card">
                    <div className="dt-card__top">
                      <div>
                        <h3 className="dt-card__name">
                          {table?.name || "Untitled table"}
                        </h3>
                        <p className="dt-card__meta">
                          ID {table?.tableID || "—"} · {table?.chairs ?? 0}{" "}
                          chairs ·{" "}
                          <span className="dt-type">{table?.tableType || "—"}</span>
                        </p>
                      </div>
                      <Switch
                        checked={isActive}
                        loading={
                          statusBusyId != null &&
                          String(statusBusyId) === String(rowId)
                        }
                        onChange={(checked) =>
                          handleToggleStatus(table, checked)
                        }
                      />
                    </div>
                    <div className="dt-card__actions">
                      <button
                        type="button"
                        className="dt-edit-btn"
                        onClick={() => openEdit(table)}
                      >
                        <Fi.FiEdit3 /> Edit table
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>

            <TablePagination
              page={paged.page}
              pageSize={DEFAULT_PAGE_SIZE}
              total={paged.total}
              onPageChange={setPage}
            />
          </>
        )}
      </section>
    </Fragment>
  );
}

export default DiningTablesPanel;
